import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { userService, userWellnessService } from '../services/api';
import Navigation from '../components/Navigation';
import { Button, Card, Input } from '../components/ui';
import FaithSettings from '../components/FaithSettings';
import NotificationSettings from '../components/NotificationSettings';
import EmergencyContactSettings from '../components/EmergencyContactSettings';
import WellnessAreaSelector from '../components/WellnessAreaSelector';

const TIMEZONE_OPTIONS = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Phoenix',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Africa/Johannesburg',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney'
];

const LICENSE_TYPE_OPTIONS = [
  'LPC',
  'LPC-S',
  'LCSW',
  'LMFT',
  'LMFT-S',
  'LCDC',
  'PsyD',
  'Psychiatrist',
  'Psychologist',
  'MD',
  'PA',
  'NP',
  'Life Coach'
];

const formatDateInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const toIsoDate = (value) => (value ? new Date(value).toISOString() : '');

const buildProfileForm = (user) => ({
  firstName: user.firstName || '',
  lastName: user.lastName || '',
  title: user.title || '',
  phone: user.phone || '',
  timezone: user.timezone || 'America/New_York',
  reminderTime: user.reminderTime || '09:00',
  emailNotifications: user.emailNotifications !== false,
  licenseNumber: user.licenseNumber || '',
  licenseType: user.licenseType || '',
  licenseState: user.licenseState || '',
  licenseExpiration: formatDateInput(user.licenseExpiration),
  city: user.city || '',
  state: user.state || '',
  ordinationChurch: user.ordinationChurch || '',
  ordinationYear: user.ordinationYear || '',
  licenses: (user.licenses || []).map((license) => ({
    licenseNumber: license?.licenseNumber || '',
    licenseType: license?.licenseType || '',
    licenseState: license?.licenseState || '',
    licenseExpiration: formatDateInput(license?.licenseExpiration)
  }))
});

const ProfileSettings = () => {
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState(buildProfileForm(user));
  const [consentData, setConsentData] = useState({
    shareEngagement: user.consent?.shareEngagement ?? user.consentToShare ?? false,
    shareMoodCraving: user.consent?.shareMoodCraving ?? false,
    shareJournalEntries: user.consent?.shareJournalEntries ?? user.shareJournalWithClinician ?? false,
    shareFaithEntries: user.consent?.shareFaithEntries ?? false
  });
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);
  const [consentMessage, setConsentMessage] = useState(null);
  const [consentSaving, setConsentSaving] = useState(false);
  const [clinicians, setClinicians] = useState([]);
  const [clinicianMessage, setClinicianMessage] = useState(null);
  const [selectedClinician, setSelectedClinician] = useState(user.assignedClinician?._id || '');
  const [assigning, setAssigning] = useState(false);
  const [unassigning, setUnassigning] = useState(false);
  const [unassignRequestedAt, setUnassignRequestedAt] = useState(user.unassignRequestedAt || null);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportMessage, setExportMessage] = useState(null);
  const [exportPreview, setExportPreview] = useState(null);
  const [showExportPreview, setShowExportPreview] = useState(false);
  const [homeworkPreferences, setHomeworkPreferences] = useState({
    preferredPillar: user.homeworkPreferences?.preferredPillar || 'any',
    excludeFaith: user.homeworkPreferences?.excludeFaith || false,
    difficultyLevel: user.homeworkPreferences?.difficultyLevel || 'adaptive'
  });
  const [homeworkPrefsSaving, setHomeworkPrefsSaving] = useState(false);
  const [homeworkPrefsMessage, setHomeworkPrefsMessage] = useState(null);

  useEffect(() => {
    if (user.role !== 'client') return;

    const loadClinicians = async () => {
      try {
        const response = await userService.getClinicians(false);
        setClinicians(response.data.data.clinicians || []);
        setUnassignRequestedAt(user.unassignRequestedAt || null);
      } catch (error) {
        setClinicianMessage({
          type: 'error',
          text: error.response?.data?.message || 'Unable to load clinicians.'
        });
      }
    };

    loadClinicians();
  }, [user.role, user.unassignRequestedAt]);

  useEffect(() => {
    setFormData(buildProfileForm(user));
    setHomeworkPreferences({
      preferredPillar: user.homeworkPreferences?.preferredPillar || 'any',
      excludeFaith: user.homeworkPreferences?.excludeFaith || false,
      difficultyLevel: user.homeworkPreferences?.difficultyLevel || 'adaptive'
    });
  }, [user]);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: e.target.type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const hasPartialLicense = formData.licenses.some((license) => {
        const fields = [license.licenseType, license.licenseNumber, license.licenseState, license.licenseExpiration];
        const filled = fields.filter(Boolean).length;
        return filled > 0 && filled < fields.length;
      });

      if (hasPartialLicense) {
        setMessage({
          type: 'error',
          text: 'Complete all fields for each additional license or remove the entry.'
        });
        setSaving(false);
        return;
      }

      const payload = {
        ...formData,
        phone: formData.phone.trim(),
        licenseNumber: formData.licenseNumber.trim(),
        licenseState: formData.licenseState.trim().toUpperCase(),
        licenseExpiration: toIsoDate(formData.licenseExpiration),
        city: formData.city.trim(),
        state: formData.state.trim().toUpperCase(),
        ordinationChurch: formData.ordinationChurch.trim(),
        ordinationYear: formData.ordinationYear ? Number(formData.ordinationYear) : null,
        licenses: formData.licenses
          .filter((license) => license.licenseType && license.licenseNumber && license.licenseState && license.licenseExpiration)
          .map((license) => ({
            licenseNumber: license.licenseNumber.trim(),
            licenseType: license.licenseType,
            licenseState: license.licenseState.trim().toUpperCase(),
            licenseExpiration: toIsoDate(license.licenseExpiration)
          }))
      };

      const response = await userService.updateProfile(user.id, payload);
      updateUser(response.data.data.user);
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (error) {
      const validationError = error.response?.data?.errors?.[0];
      setMessage({
        type: 'error',
        text: validationError
          ? `${validationError.field}: ${validationError.message}`
          : (error.response?.data?.message || 'Failed to update profile')
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAdditionalLicenseChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedLicenses = [...prev.licenses];
      updatedLicenses[index] = {
        ...updatedLicenses[index],
        [field]: value
      };
      return {
        ...prev,
        licenses: updatedLicenses
      };
    });
  };

  const handleAddLicense = () => {
    setFormData((prev) => ({
      ...prev,
      licenses: [
        ...prev.licenses,
        {
          licenseNumber: '',
          licenseType: '',
          licenseState: '',
          licenseExpiration: ''
        }
      ]
    }));
  };

  const handleRemoveLicense = (index) => {
    setFormData((prev) => ({
      ...prev,
      licenses: prev.licenses.filter((_, licenseIndex) => licenseIndex !== index)
    }));
  };

  const handleConsentChange = (e) => {
    const { name, checked } = e.target;
    setConsentData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleConsentSave = async () => {
    setConsentMessage(null);
    setConsentSaving(true);

    try {
      const response = await userService.updateConsent(user.id, consentData);
      updateUser({
        ...user,
        consent: response.data.data.consent,
        consentToShare: response.data.data.consentToShare,
        shareJournalWithClinician: response.data.data.shareJournalWithClinician
      });
      setConsentMessage({ type: 'success', text: 'Consent preferences updated.' });
    } catch (error) {
      setConsentMessage({
        type: 'error',
        text: error.response?.data?.message || 'Unable to update consent preferences.'
      });
    } finally {
      setConsentSaving(false);
    }
  };

  const handleUnenroll = async () => {
    setActionMessage(null);
    try {
      const response = await userService.unenroll(user.id);
      if (response.data?.data?.user) {
        updateUser(response.data.data.user);
      }
      setActionMessage({ type: 'success', text: 'You have been unenrolled.' });
    } catch (error) {
      setActionMessage({
        type: 'error',
        text: error.response?.data?.message || 'Unable to unenroll.'
      });
    }
  };

  const handleDeleteAccount = async () => {
    setActionMessage(null);
    try {
      await userService.deleteAccount(user.id);
      localStorage.removeItem('token');
      window.location.href = '/';
    } catch (error) {
      setActionMessage({
        type: 'error',
        text: error.response?.data?.message || 'Unable to delete account.'
      });
    }
  };

  const handleAssignClinician = async () => {
    setClinicianMessage(null);

    if (!selectedClinician) {
      setClinicianMessage({
        type: 'error',
        text: 'Please select a clinician before continuing.'
      });
      return;
    }

    setAssigning(true);
    try {
      const response = await userService.assignClinician(user.id, selectedClinician);
      updateUser({ ...user, assignedClinician: response.data.data.assignedClinician });
      setUnassignRequestedAt(null);
      setClinicianMessage({
        type: 'success',
        text: 'Clinician assigned successfully.'
      });
    } catch (error) {
      setClinicianMessage({
        type: 'error',
        text: error.response?.data?.message || 'Unable to assign clinician.'
      });
    } finally {
      setAssigning(false);
    }
  };

  const handleRequestUnassign = async () => {
    setClinicianMessage(null);

    if (!user.assignedClinician) {
      setClinicianMessage({
        type: 'error',
        text: 'No clinician assigned to request unassignment.'
      });
      return;
    }

    if (unassignRequestedAt) {
      setClinicianMessage({
        type: 'error',
        text: 'Unassignment request already pending.'
      });
      return;
    }

    setUnassigning(true);
    try {
      const response = await userService.requestClinicianUnassign(user.id);
      setUnassignRequestedAt(response.data.data.unassignRequestedAt);
      updateUser({ ...user, unassignRequestedAt: response.data.data.unassignRequestedAt });
      setClinicianMessage({
        type: 'success',
        text: 'Unassignment request sent. Your clinician must approve before removal.'
      });
    } catch (error) {
      setClinicianMessage({
        type: 'error',
        text: error.response?.data?.message || 'Unable to request unassignment.'
      });
    } finally {
      setUnassigning(false);
    }
  };

  const handleLoadExportPreview = async () => {
    setExportMessage(null);

    try {
      const response = await userService.getExportPreview(user.id);
      setExportPreview(response.data.data.preview);
      setShowExportPreview(true);
    } catch (error) {
      setExportMessage({
        type: 'error',
        text: error.response?.data?.message || 'Unable to load export preview.'
      });
    }
  };

  const handleHomeworkPreferenceChange = (event) => {
    const { name, value, type, checked } = event.target;
    setHomeworkPreferences((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleHomeworkPreferencesSave = async () => {
    setHomeworkPrefsMessage(null);
    setHomeworkPrefsSaving(true);
    try {
      const response = await userWellnessService.updatePreferences(user.id, homeworkPreferences);
      const saved = response?.data?.data || homeworkPreferences;
      updateUser({ ...user, homeworkPreferences: saved });
      setHomeworkPrefsMessage({ type: 'success', text: 'Homework preferences updated.' });
    } catch (error) {
      setHomeworkPrefsMessage({
        type: 'error',
        text: error.response?.data?.message || 'Unable to update homework preferences.'
      });
    } finally {
      setHomeworkPrefsSaving(false);
    }
  };

  const handleWellnessAreasSaved = (saved) => {
    updateUser({
      ...user,
      wellnessAreas: saved?.wellnessAreas || [],
      wellnessAreasSetBy: saved?.wellnessAreasSetBy || user.wellnessAreasSetBy || 'unset',
      wellnessAreasUpdatedAt: saved?.wellnessAreasUpdatedAt || user.wellnessAreasUpdatedAt
    });
  };

  const handleDownloadExport = async () => {
    setExportMessage(null);
    setExportLoading(true);

    try {
      const response = await userService.downloadDataExport(user.id);
      
      // Create a blob from the response
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.headers['x-filename'] || `mhdf-momentum-export-${new Date().toISOString().split('T')[0]}.json`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setExportMessage({
        type: 'success',
        text: 'Your data has been exported successfully. Check your downloads folder.'
      });
    } catch (error) {
      setExportMessage({
        type: 'error',
        text: error.response?.data?.message || 'Unable to export data.'
      });
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <>
      <Navigation />
      <main className="mx-auto max-w-4xl px-6 py-10">
        <Card>
          <h2 className="text-3xl font-display font-semibold text-neutral-900">Profile Settings</h2>
          <p className="mt-2 text-neutral-600">Update your preferences and contact information.</p>

          {message && (
            <div
              className={`mt-4 rounded-soft border p-3 text-sm ${
                message.type === 'success'
                  ? 'border-success-200 bg-success-50 text-success-700'
                  : 'border-error-200 bg-error-50 text-error-700'
              }`}
            >
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-6 space-y-8">
            <section>
              <h3 className="text-lg font-semibold text-purple-800">Personal Information</h3>
              <div className="mt-4 grid gap-4 md:grid-cols-3">
                <label className="block text-sm text-neutral-700">
                  <span className="mb-2 block font-medium">Title (Optional)</span>
                  <select
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  >
                    <option value="">None</option>
                    <option value="Mr.">Mr.</option>
                    <option value="Mrs.">Mrs.</option>
                    <option value="Ms.">Ms.</option>
                    <option value="Dr.">Dr.</option>
                    <option value="Prof.">Prof.</option>
                    <option value="Rev.">Rev.</option>
                  </select>
                </label>
                <Input
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  fullWidth
                />
                <Input
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  fullWidth
                />
              </div>
              <div className="mt-4">
                <Input
                  label="Phone Number (Optional)"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1 (555) 123-4567"
                  helperText="For SMS notifications and emergency contact"
                  fullWidth
                />
              </div>
              <div className="mt-4">
                <Input
                  label="Username"
                  value={user.username}
                  disabled
                  helperText="Username cannot be changed"
                  fullWidth
                />
              </div>
              <div className="mt-4">
                <Input
                  label="Email"
                  value={user.email}
                  disabled
                  helperText="Email cannot be changed"
                  fullWidth
                />
              </div>
            </section>

            {user.role === 'client' && user.assignedClinician && (
              <section className="border-t border-neutral-200 pt-6">
                <h3 className="text-lg font-semibold text-purple-800">Program Affiliation</h3>
                <div className="mt-4 rounded-lg bg-gradient-to-r from-blue-50 to-purple-50 p-6 border border-blue-100">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white text-xl font-bold">
                      {user.assignedClinician.firstName?.[0]}{user.assignedClinician.lastName?.[0]}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-900">Active Client Of</p>
                      <p className="text-lg font-semibold text-blue-800 mt-1">
                        {user.assignedClinician.title && `${user.assignedClinician.title} `}
                        {user.assignedClinician.firstName} {user.assignedClinician.lastName}
                        {user.assignedClinician.licenseType && <span className="text-sm font-normal text-blue-600"> ({user.assignedClinician.licenseType})</span>}
                      </p>
                      {user.clientEnrollmentAcknowledgement?.enrolledByType && (
                        <p className="text-sm text-blue-700 mt-2">
                          Enrolled as a {user.clientEnrollmentAcknowledgement.enrolledByType === 'clergy' ? 'congregation participant' : 'clinical client'}
                        </p>
                      )}
                      {user.clientEnrollmentAcknowledgement?.acknowledgedAt && (
                        <p className="text-xs text-blue-600 mt-2">
                          Program acknowledged: {new Date(user.clientEnrollmentAcknowledgement.acknowledgedAt).toLocaleDateString()}
                        </p>
                      )}
                      <p className="text-xs text-blue-600 mt-2">
                        Contact: {user.assignedClinician.email}
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            )}

            <section className="border-t border-neutral-200 pt-6">
              <h3 className="text-lg font-semibold text-neutral-800">Preferences</h3>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <label className="block text-sm text-neutral-700">
                  <span className="mb-2 block font-medium">Timezone</span>
                  <select
                    name="timezone"
                    value={formData.timezone}
                    onChange={handleChange}
                    className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  >
                    {TIMEZONE_OPTIONS.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-neutral-500">Used for daily reminders</p>
                </label>
                <Input
                  label="Daily Reminder Time"
                  name="reminderTime"
                  type="time"
                  value={formData.reminderTime}
                  onChange={handleChange}
                  fullWidth
                />
              </div>
              <label className="mt-4 flex items-center gap-3 text-sm text-neutral-700">
                <input
                  type="checkbox"
                  name="emailNotifications"
                  checked={formData.emailNotifications}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                />
                Enable email notifications
              </label>
            </section>

            {user.role === 'client' && (
              <section className="border-t border-neutral-200 pt-6">
                <h3 className="text-lg font-semibold text-neutral-800">Homework Focus & Matching</h3>
                <p className="mt-2 text-sm text-neutral-600">
                  Keep your focus areas and matching preferences up to date. You can always adjust these later.
                </p>

                <div className="mt-4">
                  <WellnessAreaSelector
                    userId={user.id}
                    currentAreas={user.wellnessAreas || []}
                    setBy={user.wellnessAreasSetBy || 'unset'}
                    onSave={handleWellnessAreasSaved}
                  />
                </div>

                {homeworkPrefsMessage && (
                  <div
                    className={`mt-4 rounded-soft border p-3 text-sm ${
                      homeworkPrefsMessage.type === 'success'
                        ? 'border-success-200 bg-success-50 text-success-700'
                        : 'border-error-200 bg-error-50 text-error-700'
                    }`}
                  >
                    {homeworkPrefsMessage.text}
                  </div>
                )}

                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <label className="block text-sm text-neutral-700">
                    <span className="mb-2 block font-medium">Preferred pillar</span>
                    <select
                      name="preferredPillar"
                      value={homeworkPreferences.preferredPillar}
                      onChange={handleHomeworkPreferenceChange}
                      className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                    >
                      <option value="any">Any</option>
                      <option value="mind">Mind</option>
                      <option value="move">Move</option>
                      <option value="journal">Journal</option>
                    </select>
                  </label>

                  <label className="block text-sm text-neutral-700">
                    <span className="mb-2 block font-medium">Difficulty level</span>
                    <select
                      name="difficultyLevel"
                      value={homeworkPreferences.difficultyLevel}
                      onChange={handleHomeworkPreferenceChange}
                      className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                    >
                      <option value="adaptive">Adaptive</option>
                      <option value="foundational">Foundational</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                    </select>
                  </label>
                </div>

                <label className="mt-4 flex items-center gap-3 text-sm text-neutral-700">
                  <input
                    type="checkbox"
                    name="excludeFaith"
                    checked={homeworkPreferences.excludeFaith}
                    onChange={handleHomeworkPreferenceChange}
                    className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                  />
                  Hide faith-integrated homework items
                </label>

                <div className="mt-4">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleHomeworkPreferencesSave}
                    disabled={homeworkPrefsSaving}
                  >
                    {homeworkPrefsSaving ? 'Saving...' : 'Save Homework Preferences'}
                  </Button>
                </div>
              </section>
            )}

            {(user.role === 'clinician' || user.role === 'clergy') && (
              <section className="border-t border-neutral-200 pt-6">
                <h3 className="text-lg font-semibold text-neutral-800">Credentials</h3>
                <p className="mt-2 text-sm text-neutral-600">
                  {user.role === 'clinician'
                    ? 'Maintain your license details and practice location.'
                    : 'Share ordination details and any professional credentials.'}
                </p>

                {user.role === 'clinician' && (
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <Input
                      label="License Number"
                      name="licenseNumber"
                      value={formData.licenseNumber}
                      onChange={handleChange}
                      fullWidth
                    />
                    <label className="text-sm text-neutral-700">
                      <span className="mb-2 block font-medium">License Type</span>
                      <select
                        name="licenseType"
                        value={formData.licenseType}
                        onChange={handleChange}
                        className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                      >
                        <option value="">Select license type</option>
                        {LICENSE_TYPE_OPTIONS.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </label>
                    <Input
                      label="License State (2-letter code)"
                      name="licenseState"
                      value={formData.licenseState}
                      onChange={handleChange}
                      fullWidth
                    />
                    <Input
                      label="License Expiration"
                      name="licenseExpiration"
                      type="date"
                      value={formData.licenseExpiration}
                      onChange={handleChange}
                      fullWidth
                    />
                  </div>
                )}

                {user.role === 'clergy' && (
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <Input
                      label="Church of Ordination"
                      name="ordinationChurch"
                      value={formData.ordinationChurch}
                      onChange={handleChange}
                      fullWidth
                    />
                    <Input
                      label="Year Ordained"
                      name="ordinationYear"
                      type="number"
                      value={formData.ordinationYear}
                      onChange={handleChange}
                      fullWidth
                    />
                  </div>
                )}

                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <Input
                    label="City"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    fullWidth
                  />
                  <Input
                    label="State (2-letter code)"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    fullWidth
                  />
                </div>

                <div className="mt-6">
                  <div className="flex items-center justify-between gap-4">
                    <h4 className="text-sm font-semibold text-neutral-800">Additional Licenses</h4>
                    <Button size="sm" variant="ghost" type="button" onClick={handleAddLicense}>Add License</Button>
                  </div>
                  <div className="mt-3 space-y-4">
                    {formData.licenses.length === 0 && (
                      <p className="text-xs text-neutral-500">No additional licenses added.</p>
                    )}
                    {formData.licenses.map((license, index) => (
                      <div key={`license-${index}`} className="rounded-soft border border-neutral-200 p-4">
                        <div className="grid gap-4 md:grid-cols-2">
                          <Input
                            label="License Number"
                            value={license.licenseNumber}
                            onChange={(event) => handleAdditionalLicenseChange(index, 'licenseNumber', event.target.value)}
                            fullWidth
                          />
                          <label className="text-sm text-neutral-700">
                            <span className="mb-2 block font-medium">License Type</span>
                            <select
                              value={license.licenseType}
                              onChange={(event) => handleAdditionalLicenseChange(index, 'licenseType', event.target.value)}
                              className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                            >
                              <option value="">Select license type</option>
                              {LICENSE_TYPE_OPTIONS.map((type) => (
                                <option key={`${type}-${index}`} value={type}>{type}</option>
                              ))}
                            </select>
                          </label>
                          <Input
                            label="License State"
                            value={license.licenseState}
                            onChange={(event) => handleAdditionalLicenseChange(index, 'licenseState', event.target.value)}
                            fullWidth
                          />
                          <Input
                            label="License Expiration"
                            type="date"
                            value={license.licenseExpiration}
                            onChange={(event) => handleAdditionalLicenseChange(index, 'licenseExpiration', event.target.value)}
                            fullWidth
                          />
                        </div>
                        <div className="mt-3 flex justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            type="button"
                            onClick={() => handleRemoveLicense(index)}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            <section className="border-t border-neutral-200 pt-6">
              <h3 className="text-lg font-semibold text-neutral-800">Program Information</h3>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <Input label="Current Day" value={user.currentDay} disabled fullWidth />
                <Input label="Current Phase" value={`Phase ${user.currentPhase}`} disabled fullWidth />
              </div>
              <div className="mt-4">
                <Input
                  label="Enrollment Date"
                  value={user.enrollmentDate ? new Date(user.enrollmentDate).toLocaleDateString() : 'Not started yet - Log in to begin'}
                  disabled
                  fullWidth
                />
              </div>
            </section>

            {user.role === 'client' && (
              <section className="border-t border-neutral-200 pt-6">
                <h3 className="text-lg font-semibold text-coral-800">Consent Management</h3>
                <p className="mt-2 text-sm text-coral-700">
                  {user.assignedClinician 
                    ? 'When your clinician enrolled you, consent was automatically granted for progress monitoring. You can adjust these settings below or revoke consent at any time.'
                    : 'Choose what your clinician can access. You can change these at any time.'}
                </p>

                {consentMessage && (
                  <div
                    className={`mt-4 rounded-soft border p-3 text-sm ${
                      consentMessage.type === 'success'
                        ? 'border-success-200 bg-success-50 text-success-700'
                        : 'border-error-200 bg-error-50 text-error-700'
                    }`}
                  >
                    {consentMessage.text}
                  </div>
                )}

                <div className="mt-4 space-y-3">
                  <label className="flex items-center gap-3 text-sm text-neutral-700">
                    <input
                      type="checkbox"
                      name="shareEngagement"
                      checked={consentData.shareEngagement}
                      onChange={handleConsentChange}
                      className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>
                      Share engagement and completion stats
                      {user.assignedClinician && (
                        <span className="ml-2 text-xs text-green-600 font-medium">(Auto-granted when enrolled)</span>
                      )}
                    </span>
                  </label>
                  <label className="flex items-center gap-3 text-sm text-neutral-700">
                    <input
                      type="checkbox"
                      name="shareMoodCraving"
                      checked={consentData.shareMoodCraving}
                      onChange={handleConsentChange}
                      className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>
                      Share mood and wellness trends
                      {user.assignedClinician && (
                        <span className="ml-2 text-xs text-green-600 font-medium">(Auto-granted when enrolled)</span>
                      )}
                    </span>
                  </label>
                  <label className="flex items-center gap-3 text-sm text-neutral-700">
                    <input
                      type="checkbox"
                      name="shareJournalEntries"
                      checked={consentData.shareJournalEntries}
                      onChange={handleConsentChange}
                      className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>
                      Share journal entries (non-private entries only)
                      <span className="ml-2 text-xs text-amber-600 font-medium">(Requires your explicit permission)</span>
                    </span>
                  </label>
                  <label className="flex items-center gap-3 text-sm text-neutral-700">
                    <input
                      type="checkbox"
                      name="shareFaithEntries"
                      checked={consentData.shareFaithEntries}
                      onChange={handleConsentChange}
                      className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>
                      Share faith pillar participation
                      <span className="ml-2 text-xs text-amber-600 font-medium">(Requires your explicit permission)</span>
                    </span>
                  </label>
                  <p className="text-xs text-neutral-500 mt-1">
                    Allow your clinician to see you've enabled the optional Faith pillar
                  </p>
                </div>

                <div className="mt-4">
                  <Button
                    type="button"
                    variant="coral"
                    onClick={handleConsentSave}
                    disabled={consentSaving}
                  >
                    {consentSaving ? 'Saving...' : 'Save Consent Preferences'}
                  </Button>
                </div>
              </section>
            )}

            {user.role === 'client' && (
              <section className="border-t border-neutral-200 pt-6">
                <h3 className="text-lg font-semibold text-indigo-800">Clinician Referral</h3>
                <p className="mt-2 text-sm text-indigo-700">
                  Select the clinician who referred you. This grants them access to your account
                  and counts toward their client plan limit.
                </p>

                {clinicianMessage && (
                  <div
                    className={`mt-4 rounded-soft border p-3 text-sm ${
                      clinicianMessage.type === 'success'
                        ? 'border-success-200 bg-success-50 text-success-700'
                        : 'border-error-200 bg-error-50 text-error-700'
                    }`}
                  >
                    {clinicianMessage.text}
                  </div>
                )}

                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Available Clinicians
                    </label>
                    <select
                      value={selectedClinician}
                      onChange={(e) => setSelectedClinician(e.target.value)}
                      disabled={Boolean(unassignRequestedAt)}
                      className="w-full rounded-soft border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-800 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    >
                      <option value="">Select a clinician</option>
                      {clinicians.map((clinician) => {
                        const isCurrent = clinician._id === user.assignedClinician?._id;
                        const isDisabled = !clinician.isAvailable && !isCurrent;
                        const slotsLabel = clinician.availableSlots === null
                          ? 'Unlimited'
                          : `${clinician.availableSlots} slots left`;

                        return (
                          <option key={clinician._id} value={clinician._id} disabled={isDisabled}>
                            {clinician.firstName} {clinician.lastName} · {clinician.licenseType} · {slotsLabel}
                          </option>
                        );
                      })}
                    </select>
                    <p className="mt-2 text-xs text-neutral-500">
                      Clinicians at capacity are disabled. If you need help finding your clinician,
                      contact support.
                    </p>
                    {user.assignedClinician && (
                      <p className="mt-2 text-xs text-neutral-500">
                        Currently assigned: {user.assignedClinician.firstName} {user.assignedClinician.lastName}
                      </p>
                    )}
                  </div>

                  <div className="flex items-end">
                    <Button
                      type="button"
                      variant="indigo"
                      onClick={handleAssignClinician}
                      disabled={assigning || Boolean(unassignRequestedAt)}
                    >
                      {assigning ? 'Assigning...' : 'Assign Clinician'}
                    </Button>
                    <Button
                      type="button"
                      variant="indigo-outline"
                      onClick={handleRequestUnassign}
                      disabled={unassigning || !user.assignedClinician || Boolean(unassignRequestedAt)}
                    >
                      {unassigning ? 'Requesting...' : 'Request Unassignment'}
                    </Button>
                  </div>
                </div>

                {unassignRequestedAt && (
                  <div className="mt-4 rounded-soft border border-warning-200 bg-warning-50 p-3 text-sm text-warning-700">
                    Unassignment request pending since {new Date(unassignRequestedAt).toLocaleDateString()}.
                    Your clinician must approve before you can select a new clinician.
                  </div>
                )}
              </section>
            )}

            {user.role === 'client' && (
              <section className="border-t border-neutral-200 pt-6">
                <h3 className="text-lg font-semibold text-red-800">Emergency Contact</h3>
                <p className="mt-2 text-sm text-red-700">
                  <strong>Available to all clients:</strong> Add a trusted person who will receive critical alert 
                  notifications for your safety and wellbeing.
                  {user.assignedClinician && (
                    <span className="block mt-1">
                      Your clinician also receives alerts, and this emergency contact provides an additional safety layer.
                    </span>
                  )}
                  {!user.assignedClinician && (
                    <span className="block mt-1">
                      Since you're managing your wellness independently, this ensures someone you trust is notified if concerning patterns emerge.
                    </span>
                  )}
                </p>
                <div className="mt-4">
                  <EmergencyContactSettings user={user} />
                </div>
              </section>
            )}

            <section className="border-t border-neutral-200 pt-6">
              <h3 className="text-lg font-semibold text-amber-800">Faith & Scripture Track</h3>
              <p className="mt-2 text-sm text-amber-700">
                Optionally incorporate faith and scripture into your mental wellness journey with our 365-day devotional program.
              </p>
              <div className="mt-4">
                <FaithSettings user={user} />
              </div>
            </section>

            <section className="border-t border-neutral-200 pt-6">
              <h3 className="text-lg font-semibold text-neutral-800">Notifications & Reminders</h3>
              <p className="mt-2 text-sm text-neutral-600">
                Stay engaged with personalized wellness reminders and milestone celebrations.
              </p>
              <div className="mt-4">
                <NotificationSettings user={user} />
              </div>
            </section>

            <section className="border-t border-neutral-200 pt-6">
              <h3 className="text-lg font-semibold text-cyan-800">Data & Privacy</h3>
              <p className="mt-2 text-sm text-cyan-700">
                Download your complete mental wellness companion data in a portable JSON file. Your data includes all journal entries, progress tracking, and personal wellness information. This export is HIPAA-compliant and encrypted.
              </p>

              {exportMessage && (
                <div
                  className={`mt-4 rounded-soft border p-3 text-sm ${
                    exportMessage.type === 'success'
                      ? 'border-success-200 bg-success-50 text-success-700'
                      : 'border-error-200 bg-error-50 text-error-700'
                  }`}
                >
                  {exportMessage.text}
                </div>
              )}

              <div className="mt-4 space-y-3">
                <Button
                  type="button"
                  variant="cyan-outline"
                  onClick={handleLoadExportPreview}
                  disabled={exportLoading}
                  className="w-full md:w-auto"
                >
                  Preview Export
                </Button>
                <Button
                  type="button"
                  variant="cyan"
                  onClick={handleDownloadExport}
                  disabled={exportLoading}
                  className="w-full md:w-auto"
                >
                  {exportLoading ? 'Preparing Export...' : 'Download My Data'}
                </Button>
              </div>

              {showExportPreview && exportPreview && (
                <div className="mt-6 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
                  <h4 className="font-semibold text-neutral-800">Export Preview</h4>
                  <div className="mt-3 space-y-2 text-sm text-neutral-700">
                    <p><strong>Account Created:</strong> {new Date(exportPreview.createdAt).toLocaleDateString()}</p>
                    <p><strong>Companion Started:</strong> {new Date(exportPreview.recoveryStartDate).toLocaleDateString()}</p>
                    <p><strong>Journal Entries:</strong> {exportPreview.dataPoints.journalEntries}</p>
                    <p><strong>Lapse Reports:</strong> {exportPreview.dataPoints.lapseReports}</p>
                    <p><strong>Phase Transitions:</strong> {exportPreview.dataPoints.phaseTransitions}</p>
                    <p><strong>Total Data Points:</strong> {exportPreview.dataPoints.totalDataPoints}</p>
                    <p><strong>Estimated File Size:</strong> {exportPreview.exportSize}</p>
                    <p className="pt-2 text-neutral-600">{exportPreview.note}</p>
                  </div>
                </div>
              )}
            </section>

            <section className="border-t border-neutral-200 pt-6">
              <h3 className="text-lg font-semibold text-neutral-800">Account Actions</h3>
              <p className="mt-2 text-sm text-neutral-600">
                Unenroll or delete your account. If you are assigned to a clinician, they will be notified.
              </p>

              {actionMessage && (
                <div
                  className={`mt-4 rounded-soft border p-3 text-sm ${
                    actionMessage.type === 'success'
                      ? 'border-success-200 bg-success-50 text-success-700'
                      : 'border-error-200 bg-error-50 text-error-700'
                  }`}
                >
                  {actionMessage.text}
                </div>
              )}

              {user.role === 'client' ? (
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      if (window.confirm('Unenroll from the program? You can rejoin later.')) {
                        handleUnenroll();
                      }
                    }}
                  >
                    Unenroll
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    onClick={() => {
                      if (window.confirm('Delete your account? This action disables access.')) {
                        handleDeleteAccount();
                      }
                    }}
                  >
                    Delete Account
                  </Button>
                </div>
              ) : (
                <p className="mt-4 text-sm text-neutral-500">
                  Account actions are managed by your clinician.
                </p>
              )}
            </section>

            <Button type="submit" variant="purple" disabled={saving} className="w-full md:w-auto">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </form>
        </Card>
      </main>
    </>
  );
};

export default ProfileSettings;
